import { Category } from '../../../import/category.type';
import { DatePipe } from '@angular/common';
import { subDays, subYears, subMonths } from 'date-fns';

export class FiltersUtil {
  static handleStatusFilters(data): [any[], {}] {
    const status = {};
    const statusLabels = {};
    const tags = [];
    const searchFilter = {};

    for (const sta of data) {
      if (sta.group === undefined || sta.ignore !== undefined) continue;

      const value = sta.aggregated
        ? { status: sta.parentData, rejection: sta.data }
        : sta.data;
      const label = sta.label;

      if (!status[sta.group]) {
        status[sta.group] = [];
        statusLabels[sta.group] = [];
      }

      status[sta.group].push(value);
      statusLabels[sta.group].push(label);
    }

    for (const group of Object.keys(statusLabels)) {
      let values;
      let isLengthOnly = false;
      if (statusLabels[group].length > 4) {
        // Cas de plus de 4 éléments : afficher le nombre
        values = `${statusLabels[group].length}`;
        isLengthOnly = true;
      } else if (statusLabels[group].length === 1) {
        // Cas d'un seul élément : utiliser la valeur directement (pas un tableau)
        values = statusLabels[group][0];
      } else {
        // Cas de 2 à 4 éléments : renvoyer un tableau
        values = statusLabels[group];
      }
      tags.push({
        name: group,
        parent: 'status',
        header: FiltersUtil.translateStatus(group),
        value: statusLabels[group],
        valueString: values,
        isMultiple:
          statusLabels[group].length > 1 && statusLabels[group].length <= 4,
        isLengthOnly: isLengthOnly
      });

      searchFilter[group] = statusLabels[group];
    }

    return [tags, status];
  }

  static translateStatus(status) {
    const statusMap = {
      status: 'filters.status',
      editorialCharter: 'detailProduct.editorialCharter',
      format: 'detailProduct.format',
    };
    return statusMap[status] ?? 'filters.status';
  }

  static handleCategoryFilter(data: Category[]): [any[], {}] {
    const categories = [];
    const values = [];
    const tags = [];

    for (const cat of data) {
      if (cat.hasChildren == 'false') {
        categories.push(cat.categoryBreadcrumbLoc);
        values.push(cat.categoryLabelLoc);
      }
    }

    tags.push({
      name: 'category',
      header: 'filters.category',
      value: categories,
      valueString:
        values.length > 4
          ? `${values.length}`
          : values.join(','),
      isLengthOnly: values.length > 4
    });

    return [tags, categories];
  }

  static handleSelectFilter(
    data,
    header,
    name,
    key,
    viewName = null,
  ): [any[], {}] {
    const values = data.map((item) => item[key]);
    const valuesTag = data.map((item) =>
      viewName ? item[viewName] : item[key],
    );
    let valueString;
    let isLengthOnly = false;
    if (valuesTag.length > 4) {
      // Cas de plus de 4 éléments : afficher le nombre
      valueString = `${valuesTag.length}`;
      isLengthOnly = true;
    } else if (valuesTag.length === 1) {
      // Cas d'un seul élément : utiliser la valeur directement (pas un tableau)
      valueString = valuesTag[0];
    } else {
      // Cas de 2 à 4 éléments : renvoyer un tableau
      valueString = valuesTag;
    }
    const tags = [
      {
        name,
        header,
        value: valuesTag,
        valueString: valueString,
        isMultiple: valuesTag.length > 1 && valuesTag.length <= 4,
        isLengthOnly: isLengthOnly
      },
    ];
    return [tags, values];
  }

  static handleSingleSelectFilter(
    data,
    header,
    name,
    key,
    viewName = null,
  ): [any[], {}] {
    data = [data];
    const values = data.map((item) => item[key]);
    const valuesTag = data.map((item) =>
      viewName ? item[viewName] : item[key],
    );
    const tags = [
      {
        name,
        header,
        value: valuesTag,
        valueString:
          valuesTag.length > 4
            ? `${valuesTag.length}`
            : valuesTag.join(','),
        isLengthOnly: valuesTag.length > 4
      },
    ];
    return [tags, values];
  }

  static handleBrandFilter(data): [any[], {}] {
    const brands = data.map((brand) => brand.name);
    const tags = [
      {
        name: 'brands',
        header: 'filters.brand',
        value: brands,
        valueString:
          brands.length > 4
            ? `${brands.length}`
            : brands.join(','),
        isLengthOnly: brands.length > 4
      },
    ];
    return [tags, brands];
  }

  static handleDateFilter(data, locale = 'fr_FR'): [any[], (string | null)[]] {
    const datePipe = new DatePipe(locale.replace('_', '-'));
    const tags = [];
    let searchFilter = [null, null];

    if (data?.[0] && data?.[1]) {
      searchFilter = [
        datePipe.transform(data[0], 'dd/MM/yyyy HH:mm'),
        datePipe.transform(data[1], 'dd/MM/yyyy HH:mm'),
      ];
      tags.push({
        name: 'lastImportDate',
        header: 'filters.lastImport',
        value: data,
        valueString: `${datePipe.transform(data[0], 'dd-MM-yyyy HH:mm')} - ${datePipe.transform(data[1], 'dd-MM-yyyy HH:mm')}`,
      });
    }

    return [tags, searchFilter];
  }

  static handleTwoDatesFilter(data, header, locale = 'fr_FR') {
    return FiltersUtil.handleDates(data, header, locale, false);
  }

  static handleTwoDatesWithSecFilter(data, header, locale = 'fr_FR') {
    return FiltersUtil.handleDates(data, header, locale, true);
  }

  static handleDates(data, header, locale, includeSeconds) {
    const datePipe = new DatePipe(locale.replace('_', '-'));
    const tags = [];
    let searchFilter = [null, null];

    // Définir le format de date en fonction du paramètre includeSeconds
    const dateFormat = includeSeconds
      ? 'dd-MM-yyyy HH:mm:ss'
      : 'dd/MM/yyyy HH:mm';
    const displayFormat = includeSeconds
      ? 'dd-MM-yyyy HH:mm:ss'
      : 'dd-MM-yyyy HH:mm';

    if (data?.[0] && !data[1]) {
      searchFilter = [datePipe.transform(data[0], dateFormat), null];
      tags.push({
        name: 'dates',
        header,
        value: data,
        valueString: `${datePipe.transform(data[0], displayFormat)}`,
      });
    } else if (!data[0] && data?.[1]) {
      searchFilter = [null, datePipe.transform(data[1], dateFormat)];
      tags.push({
        name: 'dates',
        header,
        value: data,
        valueString: `${datePipe.transform(data[1], displayFormat)}`,
      });
    } else if (data?.[0] && data?.[1]) {
      searchFilter = [
        datePipe.transform(data[0], dateFormat),
        datePipe.transform(data[1], dateFormat),
      ];
      tags.push({
        name: 'dates',
        header,
        value: data,
        valueString: `${datePipe.transform(data[0], displayFormat)} - ${datePipe.transform(data[1], displayFormat)}`,
      });
    }

    return [tags, searchFilter];
  }

  static handleFilters(data, key) {
    const filterMapping = {
      status: FiltersUtil.handleStatusFilters,
      category: FiltersUtil.handleCategoryFilter,
      brands: FiltersUtil.handleSelectFilter,
      lastImportDate: FiltersUtil.handleDateFilter,
      seller: FiltersUtil.handleSelectFilter,
      role: FiltersUtil.handleSelectFilter,
      compte: FiltersUtil.handleSelectFilter,
      importStatus: FiltersUtil.handleSelectFilter,
      lastConnectionDate: FiltersUtil.handleTwoDatesFilter,
      importDate: FiltersUtil.handleTwoDatesWithSecFilter,
      shop: FiltersUtil.handleSelectFilter,
      shopSingle: FiltersUtil.handleSingleSelectFilter,
      shopMultiple: FiltersUtil.handleSelectFilter,
      shopMultipleWithShopCode: FiltersUtil.handleSelectFilter,
      aggregators: FiltersUtil.handleSelectFilter,
    };

    const handlerParams = {
      seller: ['filters.seller', 'seller', 'name'],
      role: ['Role', 'roles', 'roleCode', 'roleName'],
      compte: ['filters.accountStatus', 'status', 'key', 'name'],
      importStatus: ['filters.status', 'status', 'key', 'name'],
      lastConnectionDate: ['table.lastConnectionDate'],
      importDate: ['filters.importDate'],
      shop: ['filters.shop', 'shops', 'shopCode', 'shopName'],
      shopSingle: ['filters.shop', 'shops', 'shopCode', 'shopName'],
      // in the 3rd param put shopName when you want to send shopName to bff
      shopMultiple: ['filters.shop', 'shopMultiple', 'shopCode', 'shopName'],
      shopMultipleWithShopCode: ['filters.shop', 'shopMultipleWithShopCode', 'shopCode', 'shopName'],
      aggregators: ['shop.aggregator', 'aggregators', 'name', 'name'],
      brands: ['filters.brand', 'brands', 'name'],
    };

    const handler = filterMapping[key];
    const params = [data[key], ...(handlerParams[key] ?? [])];

    return handler ? handler(...params) : [[], {}];
  }

  static updateDateValues(since) {
    const date = new Date();
    const values = {
      day: [subDays(date, 1), new Date()],
      week: [subDays(date, 7), new Date()],
      month: [subMonths(date, 1), new Date()],
      year: [subYears(date, 1), new Date()],
    };
    return values[since] ?? [];
  }
}
